// app/AppErrorBoundary.jsx — extracted from App.js (behaviour-preserving split).
import React from "react";
import { reportClientError } from "@/lib/crashReporter";


// ─────────────────────────────────────────────────────────────────────
// Root React error boundary — prevents installed-PWA white-screens.
// ─────────────────────────────────────────────────────────────────────
// A single uncaught render error would otherwise crash the WebView on
// older Android tablets (Lenovo/Samsung/Honor). Instead we catch it,
// stash the last error in localStorage for post-mortem, and render a
// simple "tap to reload" fallback so the operator can recover in-place.
export class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, componentStack: "", showDetails: false, copied: false };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, info) {
    try {
      const log = JSON.parse(window.localStorage.getItem("nfcs.errors") || "[]");
      log.unshift({
        at: new Date().toISOString(),
        message: String((error && error.message) || error),
        stack: (error && error.stack) ? String(error.stack).slice(0, 2000) : "",
        component: (info && info.componentStack) ? String(info.componentStack).slice(0, 2000) : "",
        ua: (typeof navigator !== "undefined" && navigator.userAgent) || "",
      });
      window.localStorage.setItem("nfcs.errors", JSON.stringify(log.slice(0, 20)));
    } catch (_) { /* noop */ }
    // Also log to console in case a devtools inspector is attached.
    try { console.error("[NFCS] Uncaught render error:", error, info); } catch (_) { /* noop */ }
    // Session 43 — ship it to the server so the owner / support can see
    // crashes from every device (Developer tools → Crash reports).
    try { reportClientError("render", error, { component_stack: info && info.componentStack }); } catch (_) { /* noop */ }
    // Persist the component stack so the render can show a clickable
    // "Show details" panel without waiting for a second render pass.
    try {
      this.setState({
        componentStack: (info && info.componentStack) ? String(info.componentStack) : "",
      });
    } catch (_) { /* noop */ }
  }
  handleReload = () => {
    try { window.location.reload(); } catch (_) { /* noop */ }
  };
  handleReset = () => {
    // Escape hatch: wipe local caches (but keep device install context)
    // then reload. Solves stale-state or corrupted-IDB white-screens.
    try {
      const keepKeys = ["nfcs.business_code", "nfcs.device_role", "nfcs.device_id", "nfcs.errors"];
      const saved = {};
      keepKeys.forEach((k) => { try { const v = window.localStorage.getItem(k); if (v) saved[k] = v; } catch (_) {} });
      window.localStorage.clear();
      Object.entries(saved).forEach(([k, v]) => { try { window.localStorage.setItem(k, v); } catch (_) {} });
      window.sessionStorage.clear();
    } catch (_) { /* noop */ }
    try { window.location.reload(); } catch (_) { /* noop */ }
  };
  render() {
    if (!this.state.error) return this.props.children;
    const stack = String(
      (this.state.error && this.state.error.stack) || ""
    );
    const compStack = String(
      (this.state.componentStack) || ""
    );
    // Pick the most useful "location hint" — the first frame that
    // references our own bundle. This is what tells us WHICH .map()
    // call blew up, without the user having to open devtools.
    const firstAppFrame = (() => {
      const lines = (stack + "\n" + compStack).split("\n");
      for (const ln of lines) {
        const trimmed = ln.trim();
        if (!trimmed) continue;
        // Skip generic React frames.
        if (/react-dom|scheduler|invokeGuardedCallback/i.test(trimmed)) continue;
        if (/at\s+/.test(trimmed) || /^in\s+\w+/i.test(trimmed)) return trimmed.slice(0, 300);
      }
      return "";
    })();
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: "24px", background: "#fff", color: "#111", fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, sans-serif" }} data-testid="app-error-boundary">
        <div style={{ maxWidth: 620, textAlign: "center" }}>
          <div style={{ fontSize: 44, marginBottom: 12 }}>⚠️</div>
          <h1 style={{ fontSize: 20, fontWeight: 700, marginBottom: 8 }}>Something went wrong</h1>
          <p style={{ fontSize: 14, color: "#555", marginBottom: 20 }}>
            The app hit an unexpected error. You can reload — your business and role are preserved.
          </p>
          <div style={{ display: "flex", gap: 8, justifyContent: "center", flexWrap: "wrap" }}>
            <button onClick={this.handleReload} style={{ padding: "10px 18px", borderRadius: 12, border: "none", background: "#111", color: "#fff", fontWeight: 600, cursor: "pointer" }} data-testid="app-error-reload-btn">Reload</button>
            <button onClick={this.handleReset} style={{ padding: "10px 18px", borderRadius: 12, border: "1px solid #ccc", background: "#fff", color: "#111", fontWeight: 600, cursor: "pointer" }} data-testid="app-error-reset-btn">Reset &amp; reload</button>
            <button
              onClick={() => this.setState({ showDetails: !this.state.showDetails })}
              style={{ padding: "10px 18px", borderRadius: 12, border: "1px solid #ccc", background: "#fff", color: "#111", fontWeight: 600, cursor: "pointer" }}
              data-testid="app-error-details-btn"
            >
              {this.state.showDetails ? "Hide details" : "Show details"}
            </button>
          </div>
          <p style={{ fontSize: 11, color: "#888", marginTop: 20 }} data-testid="app-error-message">
            {String((this.state.error && this.state.error.message) || "Unknown error").slice(0, 240)}
          </p>
          {this.state.showDetails && (
            <div style={{ marginTop: 16, textAlign: "left" }}>
              {firstAppFrame ? (
                <div
                  style={{ fontSize: 12, background: "#f6f6f6", padding: "10px 12px", borderRadius: 8, marginBottom: 8, wordBreak: "break-word" }}
                  data-testid="app-error-first-frame"
                >
                  <strong style={{ display: "block", marginBottom: 4 }}>Location:</strong>
                  <code style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>{firstAppFrame}</code>
                </div>
              ) : null}
              <details style={{ fontSize: 11, color: "#333" }}>
                <summary style={{ cursor: "pointer", marginBottom: 6 }}>Full stack</summary>
                <pre
                  style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", background: "#f6f6f6", padding: "10px 12px", borderRadius: 8, maxHeight: 220, overflow: "auto", fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}
                  data-testid="app-error-stack"
                >{stack || "(no stack)"}
                </pre>
              </details>
              {compStack && (
                <details style={{ fontSize: 11, color: "#333", marginTop: 8 }}>
                  <summary style={{ cursor: "pointer", marginBottom: 6 }}>Component stack</summary>
                  <pre
                    style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", background: "#f6f6f6", padding: "10px 12px", borderRadius: 8, maxHeight: 220, overflow: "auto", fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}
                    data-testid="app-error-component-stack"
                  >{compStack}
                  </pre>
                </details>
              )}
              <button
                onClick={() => {
                  try {
                    const text = [
                      "Error: " + String((this.state.error && this.state.error.message) || ""),
                      "Location: " + (firstAppFrame || "(unknown)"),
                      "Stack:",
                      stack,
                      "",
                      "Component stack:",
                      compStack,
                    ].join("\n");
                    if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
                      navigator.clipboard.writeText(text);
                      this.setState({ copied: true });
                      window.setTimeout(() => this.setState({ copied: false }), 1500);
                    }
                  } catch (_) { /* noop */ }
                }}
                style={{ marginTop: 10, padding: "8px 14px", borderRadius: 10, border: "1px solid #ccc", background: "#fff", color: "#111", fontWeight: 600, cursor: "pointer", fontSize: 12 }}
                data-testid="app-error-copy-btn"
              >
                {this.state.copied ? "Copied!" : "Copy details"}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }
}
